/**
 * FeedbackHost — the single mount point for the in-app feedback flow.
 *
 * Rendered once inside the authenticated shell (AppShell). It owns all report state and the global
 * launch shortcut; the visible entry points (Header user menu, Sidebar drawer item) only flip
 * `useFeedbackStore.isOpen`/`intent`. There is deliberately NO always-visible floating button — the
 * owner constraint for this feature is that it must not add chrome.
 *
 * Entry intent: the entry points offer "Point at a problem" / "Request a feature" directly,
 * skipping the intermediate modal for the most common action. `intent: 'problem'` jumps straight
 * into annotate mode; `intent: 'feature'` opens the modal with `feature` preselected. Esc/Cancel out
 * of annotate falls through to the plain modal (still `isOpen`) rather than dead-ending — the same
 * fallback the modal's own "Point at the problem" button already relied on.
 *
 * The 'problem' jump sets `annotating` during render (the "adjust state while rendering" pattern —
 * see FeedbackModal's own `prevOpen`/`sessionId` handling for the sibling case), not in a `useEffect`.
 * An effect only runs after its triggering render has already committed and painted, so gating on
 * `isOpen` there let the modal paint for one frame — and for the whole brightmark chunk fetch on a
 * cold cache — before annotate mode took over. Setting `annotating` synchronously in the same render
 * that flips `isOpen` means the modal's `open` prop (`isOpen && !annotating`) is already false on the
 * very first paint.
 *
 * Annotate mode lazy-loads the brightmark capture library (`lib/brightmark/`, which pulls in
 * snapdom) on first use, hides the modal while the overlay is up, and on capture reopens the modal
 * pre-filled with an annotation chip plus the (async, best-effort) element screenshot. The modal
 * and overlay carry `data-brightmark-ui` so they are excluded from capture target resolution.
 */
import { useCallback, useEffect, useState } from 'react'
import { FeedbackModal } from './FeedbackModal'
import { MyFeedbackModal } from './MyFeedbackModal'
import { AnnotateOverlay } from './annotate/AnnotateOverlay'
import { loadBrightmark, type BrightmarkLib } from './annotate/brightmarkLazy'
import { isTypingTarget, matchesFeedbackShortcut } from './shortcut'
import {
  useFeedbackStore,
  selectIsFeedbackOpen,
  selectFeedbackIntent,
  selectFeedbackSessionId,
} from '@/stores/useFeedbackStore'
import type { BrightmarkAnnotation } from '@/lib/brightmark/types'

export function FeedbackHost() {
  const isOpen = useFeedbackStore(selectIsFeedbackOpen)
  const intent = useFeedbackStore(selectFeedbackIntent)
  const sessionId = useFeedbackStore(selectFeedbackSessionId)
  const openFeedback = useFeedbackStore((s) => s.openFeedback)
  const closeFeedback = useFeedbackStore((s) => s.closeFeedback)

  const [annotating, setAnnotating] = useState(false)
  const [lib, setLib] = useState<BrightmarkLib | null>(null)
  const [annotation, setAnnotation] = useState<BrightmarkAnnotation | null>(null)
  const [capturedMedia, setCapturedMedia] = useState<Blob | null>(null)

  // Jump straight into annotate mode on a fresh 'problem'-intent open — during render (see the
  // module doc above for why this can't be a useEffect). Keyed off `isOpen`'s own closed→open edge
  // (via the `prevIsOpen` comparison), not `sessionId`, so an Esc/Cancel back to the modal — which
  // leaves `isOpen` true and `sessionId` unchanged — does not re-trigger it.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen && intent === 'problem') {
      setAnnotating(true)
    }
  }

  // Global launch shortcut: Cmd/Ctrl+Shift+F. Inert while typing and while the overlay is up
  // (Esc is the overlay's own exit).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!matchesFeedbackShortcut(e)) return
      if (isTypingTarget(e.target)) return
      e.preventDefault()
      openFeedback()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [openFeedback])

  const discardAnnotation = useCallback(() => {
    setAnnotation(null)
    setCapturedMedia(null)
  }, [])

  const handleClose = useCallback(() => {
    setAnnotating(false)
    discardAnnotation()
    closeFeedback()
  }, [closeFeedback, discardAnnotation])

  // Entering annotate mode (from either the intent jump above or the modal's own "Point at the
  // problem" button, which just flips `annotating`) only marks intent; the actual lazy chunk load
  // happens here, off the render path. A stale resolution after an Esc/Cancel (or the flow closing
  // entirely) is dropped via the cleanup flag instead of calling setState on an abandoned attempt.
  useEffect(() => {
    if (!annotating || lib) return
    let cancelled = false
    loadBrightmark()
      .then((loaded) => {
        if (!cancelled) setLib(loaded)
      })
      .catch(() => {
        // Chunk load failed (offline / stale deploy) — fall back to the plain report form.
        if (!cancelled) setAnnotating(false)
      })
    return () => {
      cancelled = true
    }
  }, [annotating, lib])

  const startAnnotate = useCallback(() => {
    setAnnotating(true)
  }, [])

  const handleCaptured = useCallback(
    (captured: BrightmarkAnnotation, target: Element) => {
      setAnnotating(false)
      setAnnotation(captured)
      setCapturedMedia(null)
      if (!lib) return
      // Best-effort raster: a taint/failure resolves to null and the report goes without it.
      lib
        .captureElement(target)
        .then((shot) => {
          if (!shot) return
          try {
            setCapturedMedia(lib.dataUrlToBlob(shot.data_url))
          } catch {
            /* malformed data URL — screenshot is optional */
          }
        })
        .catch(() => {
          /* tolerated — the anchor is the durable record */
        })
    },
    [lib]
  )

  return (
    <>
      {annotating && lib && (
        <AnnotateOverlay
          lib={lib}
          onCaptured={handleCaptured}
          onCancel={() => setAnnotating(false)}
        />
      )}
      <FeedbackModal
        open={isOpen && !annotating}
        onClose={handleClose}
        initialType={intent === 'feature' ? 'feature' : 'bug'}
        sessionId={sessionId}
        annotation={annotation}
        capturedMedia={capturedMedia}
        onStartAnnotate={startAnnotate}
        onDiscardAnnotation={discardAnnotation}
      />
      <MyFeedbackModal />
    </>
  )
}
